import React, { useState } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Fade,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useTranslation } from 'react-i18next';

const L = {
  es: {
    aria: 'Herramienta interactiva TFSA vs RRSP',
    badge: 'Interactivo',
    title: 'TFSA o RRSP?',
    subtitle: '2 preguntas para saber por dónde empezar.',
    q1: 'Quiero:',
    short: 'Ahorrar corto plazo',
    shortAria: 'Ahorrar a corto plazo',
    retirement: 'Retiro',
    retirementAria: 'Ahorrar para el retiro',
    q2: 'Necesito:',
    taxBreak: 'Bajar impuestos este año',
    taxBreakAria: 'Bajar impuestos este año',
    flexibility: 'Flexibilidad',
    flexibilityAria: 'Flexibilidad para retirar',
    verdict: 'te conviene más',
    rec: {
      short_flexibility: 'Para metas a corto plazo donde necesitas poder retirar sin penalización, la TFSA es ideal. Tus ganancias crecen libres de impuestos y tienes acceso al dinero cuando lo necesites.',
      short_tax_break: 'Aunque quieres bajar impuestos, para metas a corto plazo la TFSA es mejor. Si retiras del RRSP antes del retiro, pagas impuestos + penalización. Considera la TFSA ahora y el RRSP cuando tu ingreso sea más alto.',
      retirement_flexibility: 'Si valoras la flexibilidad incluso para el retiro, la TFSA no tiene reglas de retiro forzado ni impuestos al sacar tu dinero. Ideal si tu ingreso actual no es alto o si ya contribuyes al RRSP.',
      retirement_tax_break: 'Si buscas reducir tu ingreso gravable este año y ahorras para el retiro, el RRSP es tu mejor aliado. Cada dólar que contribuyes reduce tu impuesto hoy, y el dinero crece tax-deferred hasta que lo retires.',
    },
  },
  en: {
    aria: 'Interactive TFSA vs RRSP tool',
    badge: 'Interactive',
    title: 'TFSA or RRSP?',
    subtitle: '2 questions to know where to start.',
    q1: 'I want to:',
    short: 'Save short-term',
    shortAria: 'Save for the short term',
    retirement: 'Retirement',
    retirementAria: 'Save for retirement',
    q2: 'I need:',
    taxBreak: 'Lower taxes this year',
    taxBreakAria: 'Lower taxes this year',
    flexibility: 'Flexibility',
    flexibilityAria: 'Flexibility to withdraw',
    verdict: 'is better for you',
    rec: {
      short_flexibility: 'For short-term goals where you need to withdraw without penalty, the TFSA is ideal. Your gains grow tax-free and you have access to your money whenever you need it.',
      short_tax_break: 'Even though you want to lower taxes, for short-term goals the TFSA is better. If you withdraw from an RRSP before retirement, you pay taxes + penalties. Consider a TFSA now and the RRSP when your income is higher.',
      retirement_flexibility: 'If you value flexibility even for retirement, the TFSA has no forced withdrawal rules or taxes when you take your money out. Ideal if your current income isn\'t high or if you already contribute to an RRSP.',
      retirement_tax_break: 'If you want to reduce your taxable income this year and save for retirement, the RRSP is your best ally. Every dollar you contribute reduces your taxes today, and the money grows tax-deferred until you retire.',
    },
  },
  fr: {
    aria: 'Outil interactif CÉLI vs REER',
    badge: 'Interactif',
    title: 'CÉLI ou REER\u00a0?',
    subtitle: '2 questions pour savoir par où commencer.',
    q1: 'Je veux\u00a0:',
    short: 'Épargner à court terme',
    shortAria: 'Épargner à court terme',
    retirement: 'Retraite',
    retirementAria: 'Épargner pour la retraite',
    q2: "J'ai besoin de\u00a0:",
    taxBreak: 'Réduire mes impôts cette année',
    taxBreakAria: 'Réduire mes impôts cette année',
    flexibility: 'Flexibilité',
    flexibilityAria: 'Flexibilité pour retirer',
    verdict: 'vous convient mieux',
    rec: {
      short_flexibility: 'Pour des objectifs à court terme où vous devez retirer sans pénalité, le CÉLI est idéal. Vos gains croissent libres d\'impôt et vous avez accès à votre argent quand vous le voulez.',
      short_tax_break: 'Même si vous voulez réduire vos impôts, pour des objectifs à court terme le CÉLI est mieux. Si vous retirez du REER avant la retraite, vous payez impôts + pénalité. Considérez le CÉLI maintenant et le REER quand votre revenu sera plus élevé.',
      retirement_flexibility: 'Si vous valorisez la flexibilité même pour la retraite, le CÉLI n\'a pas de règles de retrait forcé ni d\'impôts quand vous retirez. Idéal si votre revenu actuel n\'est pas élevé ou si vous contribuez déjà au REER.',
      retirement_tax_break: 'Si vous cherchez à réduire votre revenu imposable cette année et épargnez pour la retraite, le REER est votre meilleur allié. Chaque dollar contribué réduit vos impôts aujourd\'hui, et l\'argent croît à impôt différé jusqu\'à la retraite.',
    },
  },
};

/**
 * Pure recommendation logic — extracted for testability.
 * Returns { pick: 'TFSA'|'RRSP', explanation: string }.
 */
export const getRecommendation = (goal, need, lang = 'es') => {
  const l = L[lang] || L.es;
  const key = `${goal}_${need}`;
  if (goal === 'short') {
    return { pick: 'TFSA', explanation: l.rec[key] || l.rec.short_flexibility };
  }
  if (need === 'flexibility') {
    return { pick: 'TFSA', explanation: l.rec[key] || l.rec.retirement_flexibility };
  }
  return { pick: 'RRSP', explanation: l.rec.retirement_tax_break };
};

const TFSAorRRSPHelper = ({ onRecommendation }) => {
  const { i18n } = useTranslation();
  const lng = i18n.language || 'es';
  const l = L[lng] || L.es;
  const [goal, setGoal] = useState(null);
  const [need, setNeed] = useState(null);

  const showResult = goal && need;
  const result = showResult ? getRecommendation(goal, need, lng) : null;

  // Notify parent of the recommendation to highlight the corresponding card
  React.useEffect(() => {
    if (onRecommendation) {
      onRecommendation(result?.pick || null);
    }
  }, [result, onRecommendation]);

  const toggleBtnSx = {
    textTransform: 'none',
    px: 2.5,
    py: { xs: 1.5, sm: 1 },
    minHeight: 44,
    transition: 'all 0.15s ease',
    '&.Mui-selected': {
      bgcolor: '#F7F9FC',
      color: '#1E3A5F',
      borderColor: '#1E3A5F !important',
      '&:hover': { bgcolor: '#E6E9EE' },
    },
    '&.Mui-disabled': { opacity: 0.4 },
    '&:focus-visible': { outline: '2px solid #1E3A5F', outlineOffset: 2 },
  };

  return (
    <Box
      role="region"
      aria-label={l.aria}
      sx={{
        bgcolor: '#FFFFFF',
        border: '1px solid #E6E9EE',
        borderRadius: '16px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
        p: { xs: 3, md: 4 },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <HelpOutlineIcon sx={{ fontSize: '1.1rem', color: '#F9A825' }} />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            color: '#5F6B7A',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          {l.badge}
        </Typography>
      </Box>
      <Typography
        variant="h6"
        component="h3"
        sx={{ fontWeight: 700, color: '#1A1A1A', mb: 0.5 }}
      >
        {l.title}
      </Typography>
      <Typography sx={{ fontSize: '14px', color: '#5F6B7A', mb: 3 }}>
        {l.subtitle}
      </Typography>

      {/* Question 1: Goal */}
      <Typography id="goal-label" sx={{ fontSize: '14px', fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
        {l.q1}
      </Typography>
      <ToggleButtonGroup
        value={goal}
        exclusive
        onChange={(_, v) => v && setGoal(v)}
        aria-labelledby="goal-label"
        sx={{
          mb: 3,
          gap: 1,
          flexWrap: 'wrap',
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid #E6E9EE !important',
            borderRadius: '10px !important',
            m: 0,
          },
        }}
      >
        <ToggleButton value="short" aria-label={l.shortAria} sx={toggleBtnSx}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{l.short}</Typography>
        </ToggleButton>
        <ToggleButton value="retirement" aria-label={l.retirementAria} sx={toggleBtnSx}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{l.retirement}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Question 2: Need */}
      <Typography
        id="need-label"
        sx={{
          fontSize: '14px',
          fontWeight: 600,
          color: goal ? '#1A1A1A' : '#5F6B7A',
          mb: 1,
          transition: 'color 0.15s ease',
        }}
      >
        {l.q2}
      </Typography>
      <ToggleButtonGroup
        value={need}
        exclusive
        onChange={(_, v) => v && setNeed(v)}
        aria-labelledby="need-label"
        sx={{
          mb: 2,
          gap: 1,
          flexWrap: 'wrap',
          '& .MuiToggleButtonGroup-grouped': {
            border: '1px solid #E6E9EE !important',
            borderRadius: '10px !important',
            m: 0,
          },
        }}
      >
        <ToggleButton value="tax_break" disabled={!goal} aria-label={l.taxBreakAria} sx={toggleBtnSx}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{l.taxBreak}</Typography>
        </ToggleButton>
        <ToggleButton value="flexibility" disabled={!goal} aria-label={l.flexibilityAria} sx={toggleBtnSx}>
          <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{l.flexibility}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Result */}
      {showResult && result && (
        <Fade in timeout={400}>
          <Box
            role="status"
            aria-live="polite"
            sx={{
              mt: 1,
              p: 2.5,
              bgcolor: result.pick === 'TFSA' ? '#F7F9FC' : '#FFF7ED',
              borderRadius: '12px',
              border: '2px solid',
              borderColor: result.pick === 'TFSA' ? '#1E3A5F' : '#7D5A00',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Box
                sx={{
                  px: 1.5,
                  py: 0.25,
                  borderRadius: '6px',
                  bgcolor: result.pick === 'TFSA' ? '#1E3A5F' : '#7D5A00',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                }}
              >
                {result.pick === 'TFSA' ? 'TFSA' : 'RRSP'}
              </Box>
              <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#1A1A1A' }}>
                {l.verdict}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '14px', color: '#1A1A1A', lineHeight: 1.6 }}>
              {result.explanation}
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default TFSAorRRSPHelper;
